// The client deliverable, exported as files: a PDF and a PowerPoint deck.
//
// Both are drawn from the same `report` object that PresentationView renders (see
// buildReport below), so the three can never say different things. Neither is a
// screenshot of the page. The PDF is drawn with jsPDF as real text and shapes — sharp at
// any zoom, searchable, small — and the deck is native PowerPoint shapes and text the
// consultant can still edit before the meeting.
//
// Colour follows the on-screen sheet: the tenant's primary and accent for identity, and
// the rating shades (SHADES, mirrored in Reviews.css) filling every rated area.

import { RATING_LABEL } from "./ratings";

// Keep in step with the --shade-* tokens on .v2-deliv in Reviews.css.
export const SHADES = {
  red:     { bg: "#fbe3e3", edge: "#c9413f", ink: "#9b2c2b" },
  yellow:  { bg: "#fcf0d2", edge: "#d69a1f", ink: "#8a5d06" },
  green:   { bg: "#dff2e8", edge: "#2b9874", ink: "#1d6b51" },
  na:      { bg: "#eef1f5", edge: "#94a0b2", ink: "#5b6676" },
  unrated: { bg: "#ffffff", edge: "#d5dbe4", ink: "#8b95a6" },
};

const INK = "#17243d";
const BODY = "#3d4a5e";
const MUTED = "#6f7b8f";
const RULE = "#e2e7ee";
const PANEL = "#f5f7fa";

const DISCLAIMER =
  "This assessment provides technology guidance based on information available at the review date. It is not a guarantee against every operational or security risk.";

const BLANK = {
  unrated: "This area has not yet been assessed.",
  na: "Out of scope for this review.",
  rated: "No statement written for this item yet.",
};
export const blankFor = (rating) => BLANK[rating] || BLANK.rated;

const DIST = ["red", "yellow", "green", "na", "unrated"];

/** Everything the deliverable shows, gathered once so the page and both files agree. */
export function buildReport({
  assessment, score, counts, tenant, me, logo, primary, accent,
  summary, glance, findings, steps, plan, progress, previousMonth, footerLine, contact,
  title, lede, preparedDate,
}) {
  const delta = progress?.point_change;
  return {
    tenant,
    logo,
    primary,
    accent,
    organization: assessment.organization_name || "",
    eyebrow: assessment.title || "",
    title,
    lede,
    preparedDate,
    preparedBy: me?.name || "",
    score: score === null || score === undefined ? null : Math.round(Number(score)),
    counts: counts || null,
    delta: delta === null || delta === undefined ? null
      : `${delta > 0 ? `Up ${delta}` : delta < 0 ? `Down ${Math.abs(delta)}` : "No change"}${previousMonth ? ` since ${previousMonth}` : ""}`,
    deltaDown: Number(delta) < 0,
    progress: progress?.tally || null,
    summary,
    glance: glance.map(({ category, items }) => ({
      name: category.name,
      items: items.map((i) => ({ name: i.name, rating: i.rating, summary: i.summary || "" })),
    })),
    findings: findings.map(({ category, items }) => ({
      name: category.name,
      items: items.map((i) => ({
        name: i.name,
        rating: i.rating,
        summary: i.summary || "",
        impact: i.business_impact || "",
        recommendation: i.recommendation || "",
        verified: i.source === "scan",
      })),
    })),
    steps: {
      total: steps.total,
      items: steps.items.map((i) => ({
        name: i.name, rating: i.rating, recommendation: i.recommendation || "", impact: i.business_impact || "",
      })),
    },
    plan: (plan || "").trim(),
    disclaimer: DISCLAIMER,
    footerLine,
    contact: contact || me?.email || "",
  };
}

// ── Shared helpers ───────────────────────────────────────────────────────────────────

const fileBase = (report) =>
  [report.organization, report.eyebrow || "Business review"]
    .filter(Boolean).join(" - ")
    .replace(/[\\/:*?"<>|]+/g, "").replace(/\s+/g, " ").trim() || "Business review";

const hexToRgb = (hex) => {
  const h = String(hex || "#000000").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h.padEnd(6, "0");
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) || 0);
};

// A colour the browser understands may not be one the file formats do (rgb(), names).
// Normalised through a canvas so a tenant who typed "navy" still gets navy.
const toHex = (color, fallback) => {
  try {
    const ctx = document.createElement("canvas").getContext("2d");
    ctx.fillStyle = fallback;
    ctx.fillStyle = color || fallback;
    const v = ctx.fillStyle;
    return v.startsWith("#") ? v : fallback;
  } catch {
    return fallback;
  }
};

// The tenant logo as a data URL plus its proportions, or null. A logo that will not load
// (cross-origin, gone) leaves the tenant's name in its place rather than failing the export.
async function loadLogo(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = dataUrl;
    });
    // Re-encoded as PNG: both libraries take PNG and JPEG, and logos arrive as SVG or WebP.
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || 400;
    canvas.height = img.naturalHeight || 100;
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    return { data: canvas.toDataURL("image/png"), ratio: canvas.width / canvas.height };
  } catch {
    return null;
  }
}

const clip = (text, max) => {
  const t = String(text || "").replace(/\s+/g, " ").trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
};

const verdictOf = (rating) => (rating === "unrated" ? "" : (RATING_LABEL[rating] || "").toUpperCase());

// ── PDF ──────────────────────────────────────────────────────────────────────────────

/** Builds the PDF and returns the jsPDF document, unsaved. */
export async function renderPdf(report) {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "pt", format: "letter", compress: true });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 40;
  const CW = W - M * 2;
  const FOOT = 40;
  const primary = toHex(report.primary, "#093070");
  const accent = toHex(report.accent, "#6fca42");
  const logo = await loadLogo(report.logo);

  const fill = (hex) => doc.setFillColor(...hexToRgb(hex));
  const stroke = (hex) => doc.setDrawColor(...hexToRgb(hex));
  const color = (hex) => doc.setTextColor(...hexToRgb(hex));
  const font = (size, style = "normal") => { doc.setFont("helvetica", style); doc.setFontSize(size); };
  const lh = (size) => size * 1.32;
  const wrap = (text, width, size, style) => { font(size, style); return doc.splitTextToSize(String(text || ""), width); };

  let y = 0;

  const band = (tall) => {
    const h = tall ? 46 : 26;
    fill(primary); doc.rect(0, 0, W, h, "F");
    if (logo && tall) {
      const lhgt = 22; const lw = Math.min(140, lhgt * logo.ratio);
      doc.addImage(logo.data, "PNG", M, (h - lhgt) / 2, lw, lhgt);
    } else {
      color("#ffffff"); font(tall ? 11 : 8, "bold");
      doc.text(report.tenant, M, h / 2 + (tall ? 4 : 3));
    }
    if (!tall) {
      color("#ffffff"); font(8);
      doc.text(report.organization, W - M, h / 2 + 3, { align: "right" });
    }
    return h;
  };

  const newPage = () => { doc.addPage(); y = band(false) + 26; };
  const ensure = (h) => { if (y + h > H - FOOT - 10) newPage(); };

  const eyebrow = (text, at = y, x = M) => {
    color(accent); font(7.5, "bold");
    doc.text(String(text).toUpperCase(), x, at, { charSpace: 1.1 });
  };
  const h2 = (text) => {
    color(primary); font(15, "bold");
    doc.text(text, M, y); y += 20;
  };
  const section = (eyebrowText, heading, needs = 80) => {
    ensure(needs + 40);
    y += 24;
    stroke(RULE); doc.setLineWidth(0.75); doc.line(M, y - 16, W - M, y - 16);
    if (eyebrowText) { eyebrow(eyebrowText); y += 15; }
    h2(heading);
  };

  // A block of wrapped lines; returns the height used.
  const lines = (arr, x, at, size, style, hex) => {
    font(size, style); color(hex);
    arr.forEach((ln, i) => doc.text(ln, x, at + i * lh(size)));
    return arr.length * lh(size);
  };

  // ── Cover ──
  y = band(true) + 40;
  const ringR = 46;
  const ringCx = W - M - 70;
  const ringCy = y + 52;
  const textW = CW - 170;

  if (report.eyebrow) { eyebrow(report.eyebrow); y += 18; }
  const titleLines = wrap(report.title, textW, 26, "bold");
  y += lines(titleLines, M, y + 8, 26, "bold", primary) + 2;
  const ledeLines = wrap(report.lede, textW, 10.5);
  y += lines(ledeLines, M, y + 6, 10.5, "normal", MUTED) + 22;

  color(MUTED); font(7.5, "bold");
  doc.text("PREPARED FOR", M, y, { charSpace: 0.8 }); y += 15;
  color(primary); font(13, "bold"); doc.text(report.organization || "—", M, y); y += 15;
  color(MUTED); font(9.5);
  if (report.preparedDate) { doc.text(report.preparedDate, M, y); y += 13; }
  if (report.preparedBy) { doc.text(`Prepared by ${report.preparedBy}`, M, y); y += 13; }

  // Score ring: a grey track and the accent arc, drawn as short segments.
  doc.setLineWidth(9); doc.setLineCap("round");
  stroke("#e7eaf0"); doc.circle(ringCx, ringCy, ringR, "S");
  if (report.score) {
    stroke(accent);
    const end = (Math.min(100, report.score) / 100) * Math.PI * 2;
    const steps = Math.max(8, Math.round(end * 24));
    for (let i = 0; i < steps; i++) {
      const a0 = -Math.PI / 2 + (end * i) / steps;
      const a1 = -Math.PI / 2 + (end * (i + 1)) / steps;
      doc.line(ringCx + ringR * Math.cos(a0), ringCy + ringR * Math.sin(a0),
               ringCx + ringR * Math.cos(a1), ringCy + ringR * Math.sin(a1));
    }
  }
  doc.setLineCap("butt");
  color(primary); font(26, "bold");
  doc.text(report.score === null ? "—" : String(report.score), ringCx, ringCy + 9, { align: "center" });
  color(MUTED); font(8.5);
  doc.text("Overall score", ringCx, ringCy + ringR + 22, { align: "center" });
  color("#8b97ab"); font(7, "bold");
  doc.text("TECHNOLOGY HEALTH", ringCx, ringCy + ringR + 34, { align: "center", charSpace: 0.8 });

  let ry = ringCy + ringR + 50;
  if (report.counts) {
    const cellW = 26;
    const startX = ringCx - (DIST.length * cellW) / 2;
    DIST.forEach((r, i) => {
      const x = startX + i * cellW;
      fill(SHADES[r].bg); stroke(SHADES[r].edge); doc.setLineWidth(0.75);
      doc.roundedRect(x + 2, ry - 8, cellW - 4, 18, 2, 2, "FD");
      color(r === "unrated" ? MUTED : SHADES[r].ink); font(9, "bold");
      doc.text(String(report.counts[r] ?? 0), x + cellW / 2, ry + 4, { align: "center" });
    });
    ry += 24;
  }
  if (report.delta) {
    color(report.deltaDown ? SHADES.red.ink : SHADES.green.ink); font(9.5, "bold");
    doc.text(report.delta, ringCx, ry + 4, { align: "center" });
    ry += 16;
  }
  y = Math.max(y, ry) + 24;

  // ── Progress ──
  if (report.progress) {
    section(null, "Progress since the last review", 50);
    const stats = [
      [report.progress.improved, "improved"], [report.progress.regressed, "regressed"],
      [report.progress.unchanged, "unchanged"], [report.progress.new, "newly assessed"],
    ];
    stats.forEach(([n, label], i) => {
      const x = M + i * (CW / 4);
      color(primary); font(22, "bold"); doc.text(String(n ?? 0), x, y + 14);
      color(MUTED); font(9); doc.text(label, x, y + 28);
    });
    y += 44;
  }

  // ── Where things stand today ──
  section("Your technology at a glance", "Where things stand today", 120);

  const execHead = wrap(report.summary.headline, CW - 32, 12, "bold");
  const execSub = wrap(report.summary.summary, CW - 32, 9.5);
  const execH = 22 + execHead.length * lh(12) + execSub.length * lh(9.5) + 12;
  ensure(execH);
  fill(PANEL); doc.roundedRect(M, y, CW, execH, 4, 4, "F");
  fill(accent); doc.rect(M, y, 3, execH, "F");
  eyebrow("Executive summary", y + 16, M + 16);
  lines(execHead, M + 16, y + 32, 12, "bold", INK);
  lines(execSub, M + 16, y + 32 + execHead.length * lh(12) + 2, 9.5, "normal", MUTED);
  y += execH + 14;

  // Legend
  let lx = M;
  ["red", "yellow", "green", "na"].forEach((r) => {
    fill(SHADES[r].bg); doc.rect(lx, y - 8, 11, 11, "F");
    fill(SHADES[r].edge); doc.rect(lx, y - 8, 2.5, 11, "F");
    color(BODY); font(8.5);
    const label = RATING_LABEL[r];
    doc.text(label, lx + 16, y + 1);
    lx += 16 + doc.getTextWidth(label) + 18;
  });
  y += 18;

  const COLS = 3;
  const GAP = 4;
  const cellW = (CW - GAP * (COLS - 1)) / COLS;
  const PAD = 9;

  report.glance.forEach((cat) => {
    ensure(70);
    y += 8;
    color(INK); font(11.5, "bold"); doc.text(cat.name, M, y);
    stroke("#cfd6e0"); doc.setLineWidth(0.75); doc.line(M, y + 6, W - M, y + 6);
    y += 16;

    for (let i = 0; i < cat.items.length; i += COLS) {
      const row = cat.items.slice(i, i + COLS).map((it) => {
        const verdict = verdictOf(it.rating);
        const name = wrap(it.name, cellW - PAD * 2 - 4, 9.5, "bold");
        const body = wrap(it.summary || blankFor(it.rating), cellW - PAD * 2 - 4, 8.5);
        const h = PAD + name.length * lh(9.5) + (verdict ? 11 : 0) + body.length * lh(8.5) + PAD - 2;
        return { it, verdict, name, body, h };
      });
      const rowH = Math.max(...row.map((c) => c.h), 46);
      ensure(rowH + GAP);
      for (const [k, c] of row.entries()) {
        const x = M + k * (cellW + GAP);
        const shade = SHADES[c.it.rating] || SHADES.unrated;
        fill(shade.bg); doc.rect(x, y, cellW, rowH, "F");
        if (c.it.rating === "unrated") { stroke(RULE); doc.setLineWidth(0.75); doc.rect(x, y, cellW, rowH, "S"); }
        fill(shade.edge); doc.rect(x, y, 3.5, rowH, "F");
        let cy = y + PAD + 7;
        cy += lines(c.name, x + PAD + 4, cy, 9.5, "bold", INK);
        if (c.verdict) {
          color(shade.ink); font(6.5, "bold");
          doc.text(c.verdict, x + PAD + 4, cy - 1, { charSpace: 0.5 });
          cy += 11;
        }
        lines(c.body, x + PAD + 4, cy, 8.5, "normal", c.it.summary ? BODY : MUTED);
      }
      y += rowH + GAP;
    }
    y += 6;
  });

  // ── Findings ──
  section("Findings & recommendations", "What the results mean", 100);
  if (!report.findings.length) {
    color(MUTED); font(9.5);
    doc.text("No items have been rated yet.", M, y); y += 18;
  }
  report.findings.forEach((cat) => {
    ensure(70);
    y += 12;
    color(INK); font(11.5, "bold"); doc.text(cat.name, M, y);
    stroke("#cfd6e0"); doc.setLineWidth(0.75); doc.line(M, y + 6, W - M, y + 6);
    y += 18;

    cat.items.forEach((it) => {
      const shade = SHADES[it.rating];
      const inner = CW - 32;
      const parts = [];
      // Clipped so one runaway statement cannot make a card taller than a page.
      if (it.summary) parts.push({ label: "", text: clip(it.summary, 900) });
      if (it.impact) parts.push({ label: "Business impact: ", text: clip(it.impact, 900) });
      if (it.recommendation) parts.push({ label: "Recommendation: ", text: clip(it.recommendation, 900) });
      if (!parts.length) parts.push({ label: "", text: "No statement written for this item yet.", muted: true });
      const blocks = parts.map((p) => ({ ...p, lines: wrap(p.label + p.text, inner, 9) }));
      const h = 30 + blocks.reduce((s, b) => s + b.lines.length * lh(9) + 4, 0) + (it.verified ? 12 : 0) + 4;
      ensure(h + 6);
      fill(shade.bg); doc.roundedRect(M, y, CW, h, 3, 3, "F");
      fill(shade.edge); doc.rect(M, y, 4, h, "F");
      color(INK); font(11, "bold"); doc.text(it.name, M + 16, y + 19);
      color(shade.ink); font(7, "bold");
      doc.text(RATING_LABEL[it.rating].toUpperCase(), W - M - 12, y + 18, { align: "right", charSpace: 0.5 });
      let by = y + 36;
      blocks.forEach((b) => {
        b.lines.forEach((ln, idx) => {
          if (idx === 0 && b.label) {
            color(primary); font(9, "bold"); doc.text(b.label.trim(), M + 16, by);
            const lw = doc.getTextWidth(b.label);
            color(BODY); font(9); doc.text(ln.slice(b.label.length), M + 16 + lw, by);
          } else {
            color(b.muted ? MUTED : BODY); font(9); doc.text(ln, M + 16, by);
          }
          by += lh(9);
        });
        by += 4;
      });
      if (it.verified) { color(MUTED); font(8, "italic"); doc.text("Verified by external scan.", M + 16, by); }
      y += h + 6;
    });
  });

  // ── Next steps ──
  if (report.steps.items.length) {
    newPage();
    eyebrow("Recommended next steps"); y += 15;
    h2("Turn findings into action");
    const lede = wrap("Focus first on the actions that reduce the greatest current risk and create a clear owner and target date for each decision.", CW, 9.5);
    y += lines(lede, M, y, 9.5, "normal", MUTED) + 10;

    report.steps.items.forEach((s, n) => {
      const shade = SHADES[s.rating];
      const rec = wrap(s.recommendation, CW - 32, 9);
      const why = s.impact ? wrap(`Why now: ${s.impact}`, CW - 32, 8.5) : [];
      const h = 30 + rec.length * lh(9) + (why.length ? why.length * lh(8.5) + 4 : 0) + 6;
      ensure(h + 6);
      fill(shade.bg); doc.roundedRect(M, y, CW, h, 3, 3, "F");
      fill(shade.edge); doc.rect(M, y, 4, h, "F");
      color(primary); font(11, "bold"); doc.text(`${n + 1}.`, M + 16, y + 19);
      color(INK); doc.text(s.name, M + 34, y + 19);
      let by = y + 34;
      by += lines(rec, M + 16, by, 9, "normal", BODY);
      if (why.length) lines(why, M + 16, by + 3, 8.5, "normal", MUTED);
      y += h + 6;
    });
    if (report.steps.total > report.steps.items.length) {
      const more = report.steps.total - report.steps.items.length;
      color(MUTED); font(8.5);
      doc.text(`${more} additional recommendation${more === 1 ? " is" : "s are"} detailed in the findings section.`, M, y + 6);
      y += 18;
    }
  }

  // ── Agreed plan ──
  if (report.plan) {
    section(null, "Agreed action plan", 60);
    const planLines = wrap(report.plan, CW, 9.5);
    planLines.forEach((ln) => { ensure(lh(9.5)); color(BODY); font(9.5); doc.text(ln, M, y); y += lh(9.5); });
    y += 8;
  }

  // ── Disclaimer ──
  const disc = wrap(report.disclaimer, CW - 24, 8.5);
  const dh = disc.length * lh(8.5) + 18;
  ensure(dh + 10);
  y += 8;
  fill(PANEL); doc.roundedRect(M, y, CW, dh, 4, 4, "F");
  lines(disc, M + 12, y + 15, 8.5, "normal", MUTED);

  // ── Footer on every page ──
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    stroke(RULE); doc.setLineWidth(0.75); doc.line(M, H - FOOT + 4, W - M, H - FOOT + 4);
    // The tenant's line on the left, contact and page on the right. The left side gets
    // whatever room the right leaves, and wraps to a second line rather than being cut.
    color(MUTED); font(7.5);
    const right = `${report.contact ? `${report.contact}  ·  ` : ""}Page ${p} of ${pages}`;
    const left = doc.splitTextToSize(report.footerLine || "", CW - doc.getTextWidth(right) - 16).slice(0, 2);
    left.forEach((ln, i) => doc.text(ln, M, H - FOOT + 17 + i * 9.5));
    doc.text(right, W - M, H - FOOT + 17, { align: "right" });
  }

  doc.setProperties({ title: fileBase(report), subject: "Technology business review", author: report.tenant, creator: report.tenant });
  return doc;
}

/** Builds the PDF and downloads it. */
export async function exportPdf(report) {
  (await renderPdf(report)).save(`${fileBase(report)}.pdf`);
}

// ── PowerPoint ───────────────────────────────────────────────────────────────────────

/** Builds the deck and returns the PptxGenJS presentation, unsaved. */
export async function renderPptx(report) {
  const { default: PptxGenJS } = await import("pptxgenjs");
  const pptx = new PptxGenJS();
  pptx.layout = "LAYOUT_WIDE"; // 13.33 x 7.5 in
  pptx.author = report.tenant;
  pptx.company = report.tenant;
  pptx.title = fileBase(report);

  const W = 13.333;
  const H = 7.5;
  const M = 0.6;
  const CW = W - M * 2;
  const FONT = "Calibri";
  const hex = (c) => String(c).replace("#", "").toUpperCase();
  const primary = hex(toHex(report.primary, "#093070"));
  const accent = hex(toHex(report.accent, "#6fca42"));
  const logo = await loadLogo(report.logo);

  // Every content slide: a thin band in the tenant's colour, the slide's heading, and the
  // footer line with a page number.
  pptx.defineSlideMaster({
    title: "CONTENT",
    background: { color: "FFFFFF" },
    objects: [
      { rect: { x: 0, y: 0, w: W, h: 0.12, fill: { color: primary } } },
      { line: { x: M, y: H - 0.5, w: CW, h: 0, line: { color: hex(RULE), width: 0.75 } } },
      { text: { text: clip(report.footerLine, 140), options: { x: M, y: H - 0.46, w: CW - 1.6, h: 0.3, fontFace: FONT, fontSize: 9, color: hex(MUTED) } } },
    ],
    slideNumber: { x: W - M - 1, y: H - 0.46, w: 1, h: 0.3, fontFace: FONT, fontSize: 9, color: hex(MUTED), align: "right" },
  });

  const heading = (slide, eyebrowText, title) => {
    if (eyebrowText) {
      slide.addText(eyebrowText.toUpperCase(), { x: M, y: 0.35, w: CW, h: 0.3, fontFace: FONT, fontSize: 11, bold: true, color: accent, charSpacing: 2 });
    }
    slide.addText(title, { x: M, y: eyebrowText ? 0.62 : 0.4, w: CW, h: 0.6, fontFace: FONT, fontSize: 28, bold: true, color: primary });
  };

  const shadedBox = (slide, { x, y, w, h, rating }) => {
    const s = SHADES[rating] || SHADES.unrated;
    slide.addShape(pptx.ShapeType.rect, {
      x, y, w, h, fill: { color: hex(s.bg) },
      line: rating === "unrated" ? { color: hex(RULE), width: 0.75 } : { type: "none" },
    });
    slide.addShape(pptx.ShapeType.rect, { x, y, w: 0.07, h, fill: { color: hex(s.edge) }, line: { type: "none" } });
    return s;
  };

  // ── Cover ──
  {
    const slide = pptx.addSlide();
    slide.background = { color: "FFFFFF" };
    slide.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: W, h: 0.8, fill: { color: primary }, line: { type: "none" } });
    if (logo) {
      const lh = 0.46;
      slide.addImage({ data: logo.data, x: M, y: 0.17, w: Math.min(2.6, lh * logo.ratio), h: lh });
    } else {
      slide.addText(report.tenant, { x: M, y: 0.15, w: 8, h: 0.5, fontFace: FONT, fontSize: 18, bold: true, color: "FFFFFF" });
    }

    if (report.eyebrow) {
      slide.addText(report.eyebrow.toUpperCase(), { x: M, y: 1.5, w: 8, h: 0.35, fontFace: FONT, fontSize: 13, bold: true, color: accent, charSpacing: 2 });
    }
    slide.addText(report.title, { x: M, y: 1.9, w: 8, h: 1.4, fontFace: FONT, fontSize: 44, bold: true, color: primary, valign: "top" });
    slide.addText(report.lede, { x: M, y: 3.35, w: 7.5, h: 0.8, fontFace: FONT, fontSize: 16, color: hex(MUTED), valign: "top" });
    slide.addText([
      { text: "PREPARED FOR", options: { fontSize: 11, bold: true, color: hex(MUTED), charSpacing: 1.5, breakLine: true } },
      { text: report.organization || "—", options: { fontSize: 22, bold: true, color: primary, breakLine: true } },
      ...(report.preparedDate ? [{ text: report.preparedDate, options: { fontSize: 13, color: hex(MUTED), breakLine: true } }] : []),
      ...(report.preparedBy ? [{ text: `Prepared by ${report.preparedBy}`, options: { fontSize: 13, color: hex(MUTED) } }] : []),
    ], { x: M, y: 4.55, w: 7.5, h: 1.9, fontFace: FONT, valign: "top" });

    // Score ring as two shapes, a grey donut and an accent arc over it. Not a chart: chart
    // parts render inconsistently between PowerPoint, Keynote and Google Slides, and a
    // missing score on the cover is the worst place for that to happen.
    const sc = Math.max(0, Math.min(100, report.score ?? 0));
    const ring = { x: 9.75, y: 1.6, w: 2.3, h: 2.3 };
    slide.addShape(pptx.ShapeType.blockArc, { ...ring, angleRange: [0, 359.9], arcThicknessRatio: 0.16, fill: { color: "E7EAF0" }, line: { type: "none" } });
    if (sc > 0) {
      // Angles run clockwise from 3 o'clock; the arc starts at 12.
      slide.addShape(pptx.ShapeType.blockArc, { ...ring, angleRange: [270, (270 + sc * 3.6) % 360 || 269.9], arcThicknessRatio: 0.16, fill: { color: accent }, line: { type: "none" } });
    }
    slide.addText(report.score === null ? "—" : String(report.score), {
      x: 9.35, y: 2.3, w: 3.1, h: 0.9, align: "center", valign: "middle", fontFace: FONT, fontSize: 48, bold: true, color: primary,
    });
    slide.addText([
      { text: "Overall score", options: { fontSize: 14, color: hex(MUTED), breakLine: true } },
      { text: "TECHNOLOGY HEALTH", options: { fontSize: 10, bold: true, color: "8B97AB", charSpacing: 1.5 } },
    ], { x: 9.35, y: 4.6, w: 3.1, h: 0.7, align: "center", fontFace: FONT });

    if (report.counts) {
      const bw = 0.5; const gap = 0.08;
      const total = DIST.length * bw + (DIST.length - 1) * gap;
      const startX = 9.35 + (3.1 - total) / 2;
      DIST.forEach((r, i) => {
        const s = SHADES[r];
        slide.addText(String(report.counts[r] ?? 0), {
          x: startX + i * (bw + gap), y: 5.4, w: bw, h: 0.4, align: "center", valign: "middle",
          fontFace: FONT, fontSize: 13, bold: true, color: hex(r === "unrated" ? MUTED : s.ink),
          fill: { color: hex(s.bg) }, line: { color: hex(s.edge), width: 0.75 },
        });
      });
    }
    if (report.delta) {
      slide.addText(report.delta, {
        x: 9.35, y: 5.9, w: 3.1, h: 0.35, align: "center", fontFace: FONT, fontSize: 13, bold: true,
        color: hex(report.deltaDown ? SHADES.red.ink : SHADES.green.ink),
      });
    }
  }

  // ── Progress ──
  if (report.progress) {
    const slide = pptx.addSlide({ masterName: "CONTENT" });
    heading(slide, null, "Progress since the last review");
    const stats = [
      [report.progress.improved, "improved", "green"], [report.progress.regressed, "regressed", "red"],
      [report.progress.unchanged, "unchanged", "na"], [report.progress.new, "newly assessed", "yellow"],
    ];
    const bw = (CW - 0.3 * 3) / 4;
    stats.forEach(([n, label, r], i) => {
      const x = M + i * (bw + 0.3);
      shadedBox(slide, { x, y: 2, w: bw, h: 2.6, rating: r });
      slide.addText([
        { text: String(n ?? 0), options: { fontSize: 60, bold: true, color: primary, breakLine: true } },
        { text: label, options: { fontSize: 18, color: hex(BODY) } },
      ], { x: x + 0.2, y: 2.1, w: bw - 0.3, h: 2.4, fontFace: FONT, align: "center", valign: "middle" });
    });
  }

  // ── Executive summary ──
  {
    const slide = pptx.addSlide({ masterName: "CONTENT" });
    heading(slide, "Your technology at a glance", "Where things stand today");
    slide.addShape(pptx.ShapeType.rect, { x: M, y: 1.65, w: CW, h: 2.4, fill: { color: hex(PANEL) }, line: { type: "none" } });
    slide.addShape(pptx.ShapeType.rect, { x: M, y: 1.65, w: 0.08, h: 2.4, fill: { color: accent }, line: { type: "none" } });
    slide.addText([
      { text: "EXECUTIVE SUMMARY", options: { fontSize: 11, bold: true, color: accent, charSpacing: 2, breakLine: true } },
      { text: report.summary.headline, options: { fontSize: 26, bold: true, color: hex(INK), breakLine: true, paraSpaceBefore: 6 } },
      { text: report.summary.summary, options: { fontSize: 16, color: hex(MUTED), paraSpaceBefore: 8 } },
    ], { x: M + 0.35, y: 1.8, w: CW - 0.6, h: 2.1, fontFace: FONT, valign: "top" });

    // The key, and how many items landed in each shade.
    const keys = ["red", "yellow", "green", "na"];
    const kw = (CW - 0.3 * 3) / 4;
    keys.forEach((r, i) => {
      const x = M + i * (kw + 0.3);
      const s = shadedBox(slide, { x, y: 4.45, w: kw, h: 1.6, rating: r });
      slide.addText([
        { text: String(report.counts?.[r] ?? 0), options: { fontSize: 36, bold: true, color: hex(s.ink), breakLine: true } },
        { text: RATING_LABEL[r], options: { fontSize: 14, color: hex(BODY) } },
      ], { x: x + 0.3, y: 4.5, w: kw - 0.4, h: 1.5, fontFace: FONT, valign: "middle" });
    });
  }

  // ── Glance: one slide per category, split when it would not fit ──
  const PER_GLANCE = 12;
  report.glance.forEach((cat) => {
    for (let start = 0; start < cat.items.length; start += PER_GLANCE) {
      const chunk = cat.items.slice(start, start + PER_GLANCE);
      const part = cat.items.length > PER_GLANCE ? ` (${Math.floor(start / PER_GLANCE) + 1} of ${Math.ceil(cat.items.length / PER_GLANCE)})` : "";
      const slide = pptx.addSlide({ masterName: "CONTENT" });
      heading(slide, "Where things stand today", `${cat.name}${part}`);
      const cols = chunk.length <= 4 ? 2 : chunk.length <= 9 ? 3 : 4;
      const rows = Math.ceil(chunk.length / cols);
      const gap = 0.14;
      const top = 1.55;
      const avail = H - 0.7 - top;
      const cw = (CW - gap * (cols - 1)) / cols;
      const ch = Math.min(1.9, (avail - gap * (rows - 1)) / rows);
      const bodyMax = Math.round(ch * cw * 22);
      chunk.forEach((it, i) => {
        const x = M + (i % cols) * (cw + gap);
        const y = top + Math.floor(i / cols) * (ch + gap);
        const s = shadedBox(slide, { x, y, w: cw, h: ch, rating: it.rating });
        const verdict = verdictOf(it.rating);
        slide.addText([
          { text: it.name, options: { fontSize: cols === 4 ? 16 : 18, bold: true, color: hex(INK), breakLine: true } },
          ...(verdict ? [{ text: verdict, options: { fontSize: 11, bold: true, color: hex(s.ink), charSpacing: 1, breakLine: true } }] : []),
          { text: clip(it.summary || blankFor(it.rating), bodyMax), options: { fontSize: cols === 4 ? 12 : 14, color: hex(it.summary ? BODY : MUTED), paraSpaceBefore: 4 } },
        ], { x: x + 0.18, y: y + 0.06, w: cw - 0.28, h: ch - 0.12, fontFace: FONT, valign: "top", margin: 2 });
      });
    }
  });

  // ── Findings: four to a slide ──
  const PER_FIND = 4;
  report.findings.forEach((cat) => {
    for (let start = 0; start < cat.items.length; start += PER_FIND) {
      const chunk = cat.items.slice(start, start + PER_FIND);
      const slide = pptx.addSlide({ masterName: "CONTENT" });
      heading(slide, "Findings & recommendations", cat.name + (start ? " (continued)" : ""));
      // Always a two-by-two grid, so a lone finding is a card rather than a whole slide.
      const gap = 0.2;
      const cols = 2;
      const rows = 2;
      const top = 1.55;
      const cw = (CW - gap * (cols - 1)) / cols;
      const ch = (H - 0.7 - top - gap * (rows - 1)) / rows;
      const budget = Math.round(cw * ch * 28);
      chunk.forEach((it, i) => {
        const x = M + (i % cols) * (cw + gap);
        const y = top + Math.floor(i / cols) * (ch + gap);
        const s = shadedBox(slide, { x, y, w: cw, h: ch, rating: it.rating });
        const parts = [
          it.summary && { label: "", text: it.summary },
          it.impact && { label: "Business impact: ", text: it.impact },
          it.recommendation && { label: "Recommendation: ", text: it.recommendation },
        ].filter(Boolean);
        const each = Math.max(80, Math.floor(budget / Math.max(1, parts.length)));
        const runs = [
          { text: it.name, options: { fontSize: 18, bold: true, color: hex(INK), breakLine: true } },
          { text: RATING_LABEL[it.rating].toUpperCase(), options: { fontSize: 11, bold: true, color: hex(s.ink), charSpacing: 1, breakLine: true } },
        ];
        if (!parts.length) {
          runs.push({ text: "No statement written for this item yet.", options: { fontSize: 14, color: hex(MUTED) } });
        }
        parts.forEach((p, k) => {
          if (p.label) runs.push({ text: p.label, options: { fontSize: 13, bold: true, color: primary, paraSpaceBefore: 6 } });
          runs.push({ text: clip(p.text, each), options: { fontSize: 13, color: hex(BODY), paraSpaceBefore: p.label ? 0 : 6, breakLine: k < parts.length - 1 || it.verified } });
        });
        if (it.verified) runs.push({ text: "Verified by external scan.", options: { fontSize: 11, italic: true, color: hex(MUTED), paraSpaceBefore: 5 } });
        slide.addText(runs, { x: x + 0.2, y: y + 0.08, w: cw - 0.32, h: ch - 0.16, fontFace: FONT, valign: "top", margin: 2 });
      });
    }
  });

  // ── Next steps ──
  if (report.steps.items.length) {
    const slide = pptx.addSlide({ masterName: "CONTENT" });
    heading(slide, "Recommended next steps", "Turn findings into action");
    const top = 1.55;
    const n = report.steps.items.length;
    const gap = 0.12;
    const rh = Math.min(1.0, (H - 0.9 - top - gap * (n - 1)) / n);
    report.steps.items.forEach((st, i) => {
      const y = top + i * (rh + gap);
      shadedBox(slide, { x: M, y, w: CW, h: rh, rating: st.rating });
      slide.addText(String(i + 1), { x: M + 0.2, y, w: 0.6, h: rh, fontFace: FONT, fontSize: 28, bold: true, color: primary, valign: "middle" });
      slide.addText([
        { text: st.name, options: { fontSize: 18, bold: true, color: hex(INK), breakLine: true } },
        { text: clip(st.recommendation, 170), options: { fontSize: 14, color: hex(BODY), breakLine: Boolean(st.impact) } },
        ...(st.impact ? [{ text: `Why now: ${clip(st.impact, 120)}`, options: { fontSize: 12, color: hex(MUTED) } }] : []),
      ], { x: M + 0.85, y, w: CW - 1.05, h: rh, fontFace: FONT, valign: "middle", margin: 2 });
    });
    if (report.steps.total > n) {
      const more = report.steps.total - n;
      slide.addText(`${more} additional recommendation${more === 1 ? " is" : "s are"} detailed in the findings.`, {
        x: M, y: H - 0.85, w: CW, h: 0.3, fontFace: FONT, fontSize: 11, color: hex(MUTED),
      });
    }
  }

  // ── Agreed plan ──
  if (report.plan) {
    const slide = pptx.addSlide({ masterName: "CONTENT" });
    heading(slide, null, "Agreed action plan");
    // One paragraph per line the consultant wrote, so a numbered plan stays numbered.
    const paras = report.plan.slice(0, 1400).split(/\r?\n/).filter((l) => l.trim());
    slide.addText(paras.map((l, i) => ({
      text: l.trim(), options: { breakLine: i < paras.length - 1, paraSpaceAfter: 10 },
    })), { x: M, y: 1.4, w: CW, h: H - 2.1, fontFace: FONT, fontSize: 20, color: hex(BODY), valign: "top", fit: "shrink" });
  }

  // ── Close ──
  {
    const slide = pptx.addSlide();
    slide.background = { color: primary };
    slide.addText("Thank you", { x: M, y: 2.2, w: CW, h: 1, fontFace: FONT, fontSize: 48, bold: true, color: "FFFFFF" });
    slide.addText([
      { text: report.tenant, options: { fontSize: 20, bold: true, breakLine: true } },
      ...(report.contact ? [{ text: report.contact, options: { fontSize: 16 } }] : []),
    ], { x: M, y: 3.3, w: CW, h: 1, fontFace: FONT, color: "FFFFFF", valign: "top" });
    slide.addText(report.disclaimer, { x: M, y: H - 1.4, w: CW, h: 0.8, fontFace: FONT, fontSize: 11, color: "D5DCE8", valign: "bottom" });
  }

  return pptx;
}

/** Builds the deck and downloads it. */
export async function exportPptx(report) {
  await (await renderPptx(report)).writeFile({ fileName: `${fileBase(report)}.pptx` });
}
